import React from 'react';
import PropTypes from 'prop-types';
import {
  ChevronDown, ChevronRight,
  Clock, BookOpen, HelpCircle, FileText, CheckSquare,
  CheckCircle2, PlayCircle, Lock,
} from 'lucide-react';

/**
 * IGEA: Module accordion — matches Dribbble exactly.
 *   [v] 01 Module Title                                       ⋮
 *       [icon] Lesson title
 *              Meta (duration/type)
 */

// Dribbble reference: clock = video, book = ebook, document = reading,
// ? (help) = quiz, checkbox (check-square) = exam/assignment.
const LESSON_ICON_MAP = {
  video: Clock,
  ebook: BookOpen,
  reading: FileText,
  quiz: HelpCircle,
  problem: HelpCircle,
  assignment: CheckSquare,
  exam: CheckSquare,
  default: FileText,
};

// Stato del modulo, derivato da quello delle sue lezioni — nessuna fonte
// nuova, lo stato per-lezione ('completed' / 'in_progress' / 'not_started')
// e' gia' calcolato da IgeaPlayerLayout. Segnalazione cliente: "i moduli
// quando sono stati completati dovremmo differenziarli in qualche modo cosi'
// capisco che fino a quel punto ho fatto tutto anche se sono chiusi" — a
// modulo chiuso oggi non c'era alcun segno diverso da quello dei moduli
// bloccati (lucchetto) o mai iniziati.
const pickModuleStatus = (lessons) => {
  if (!lessons.length) { return 'not_started'; }
  const completate = lessons.filter((l) => l.status === 'completed').length;
  if (completate === lessons.length) { return 'completed'; }
  const inCorso = completate > 0 || lessons.some((l) => l.status === 'in_progress');
  return inCorso ? 'in_progress' : 'not_started';
};

// Le lezioni senza contenuto completabile non si contano, ne' a numeratore ne'
// a denominatore: e' la regola gia' decisa dal cliente e applicata lato edX da
// `lezioni_con_contenuto` (progress_push.py). Qui arriva gia' decisa dal
// layout, che e' l'unico posto dove il dato dell'outline viene interpretato —
// il valore mancante vale "contala", mai "scartala".
const contabili = (lessons) => lessons.filter((l) => l.completabile !== false);

const MODULE_STATUS_META = {
  completed: { label: 'Modulo completato', color: '#16A34A' },
  in_progress: { label: 'Modulo in corso', color: '#7C3AED' },
  not_started: { label: 'Modulo da iniziare', color: '#9CA3AF' },
};

// Geometria dell'anello. Un `<svg>` e due cerchi: nessuna libreria nuova, e a
// 18px pesa quanto l'icona che sostituisce — la lista dei moduli non diventa
// piu' fitta su un corso da trenta moduli.
const RAGGIO = 8;
const CIRCONFERENZA = 2 * Math.PI * RAGGIO;

/**
 * L'anello di avanzamento del modulo: quante lezioni completate su quante.
 *
 * Il completamento NON e' "l'arco piu' lungo di poco". Un anello al 97% e uno
 * al 100% sono la stessa immagine a colpo d'occhio, e qui la differenza pesa:
 * in ECM il modulo completo e' la condizione che sblocca il resto. Quindi a
 * 100% l'anello lascia il posto a un segno di natura diversa — disco pieno con
 * la spunta bianca dentro, niente arco — mentre sotto il 100% resta un arco su
 * traccia grigia. Cambia il tipo di disegno, non la sua lunghezza: si distingue
 * da lontano, in scala di grigi e a 18px.
 *
 * Terzo caso, quello che l'anello da solo perderebbe: modulo iniziato ma con
 * zero lezioni finite (una lezione a meta'). L'arco sarebbe a zero, identico a
 * un modulo mai aperto — per questo, e SOLO in quel caso, dentro l'anello
 * compare un punto pieno viola. Restano tre segni distinti per tre stati, che
 * e' la garanzia data in b362151 quando i moduli chiusi hanno iniziato a dire
 * se erano completati; l'anello la eredita, non la affianca.
 */
const AnelloProgresso = ({ completate, totale, stato }) => {
  const percentuale = totale > 0 ? Math.round((completate / totale) * 100) : 0;
  const { color } = MODULE_STATUS_META[stato];

  if (stato === 'completed') {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 20 20"
        data-igea-segno="spunta"
        data-igea-percentuale={percentuale}
        aria-hidden="true"
        className="tw-flex-shrink-0"
      >
        <circle cx="10" cy="10" r="9" fill={color} />
        <path
          d="M5.8 10.3 L8.6 13.1 L14.2 7.2"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  const arco = (percentuale / 100) * CIRCONFERENZA;
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      data-igea-segno="anello"
      data-igea-percentuale={percentuale}
      aria-hidden="true"
      className="tw-flex-shrink-0"
    >
      <circle cx="10" cy="10" r={RAGGIO} fill="none" stroke="#E5E7EB" strokeWidth="2.5" />
      {arco > 0 && (
        <circle
          cx="10"
          cy="10"
          r={RAGGIO}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={`${arco} ${CIRCONFERENZA - arco}`}
          transform="rotate(-90 10 10)"
        />
      )}
      {stato === 'in_progress' && arco === 0 && (
        <circle cx="10" cy="10" r="2.5" fill={color} />
      )}
    </svg>
  );
};

AnelloProgresso.propTypes = {
  completate: PropTypes.number.isRequired,
  totale: PropTypes.number.isRequired,
  stato: PropTypes.oneOf(['completed', 'in_progress', 'not_started']).isRequired,
};

const formatMeta = (lesson) => {
  // Prefer an explicit meta string prepared by the parent layout.
  // If no meta is available, return null so the caller can skip the
  // meta row entirely — no more hardcoded "05:00" placeholders.
  if (lesson.meta) { return lesson.meta; }
  if (lesson.duration) { return lesson.duration; }
  return null;
};

// Pick the status icon shown next to the lesson title. Progressive
// unlock semantics: locked lessons show a closed padlock; completed
// ones show a green check; in-progress show a filled play circle in
// brand-purple; not-started but unlocked fall back to the type icon
// (clock for video, book for ebook, etc).
const pickStatusIcon = (lesson) => {
  if (lesson.locked) {
    return { Icon: Lock, color: '#9CA3AF' };
  }
  if (lesson.status === 'completed') {
    return { Icon: CheckCircle2, color: '#16A34A' };
  }
  if (lesson.status === 'in_progress') {
    return { Icon: PlayCircle, color: '#7C3AED' };
  }
  return {
    Icon: LESSON_ICON_MAP[lesson.type] || LESSON_ICON_MAP.default,
    color: '#6B7280',
  };
};

const IgeaModuleAccordion = ({
  number,
  module,
  currentUnitId,
  open,
  onToggle,
}) => {
  const lessons = module.lessons || [];
  const moduleLocked = !!module.locked;
  // Locked modules stay collapsed until the previous module is done: chi
  // decide l'apertura e' la sidebar (un modulo aperto per volta), ma un
  // modulo bloccato non si apre comunque.
  const aperto = open && !moduleLocked;
  const daContare = contabili(lessons);
  const moduleStatus = pickModuleStatus(daContare);
  const completedCount = daContare.filter((l) => l.status === 'completed').length;
  const { label: moduleStatusLabel } = MODULE_STATUS_META[moduleStatus];

  return (
    <div>
      <button
        type="button"
        onClick={() => !moduleLocked && onToggle()}
        disabled={moduleLocked}
        className={[
          'tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-transparent tw-border-0',
          moduleLocked ? 'tw-cursor-not-allowed tw-opacity-60' : 'tw-cursor-pointer hover:tw-bg-igea-bg',
        ].join(' ')}
        aria-expanded={aperto}
        aria-disabled={moduleLocked}
        title={moduleLocked ? 'Completa il modulo precedente per sbloccare' : undefined}
      >
        <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
          {(() => {
            if (moduleLocked) {
              return <Lock size={14} strokeWidth={2} className="tw-text-text-muted tw-flex-shrink-0" />;
            }
            if (aperto) {
              return <ChevronDown size={14} strokeWidth={2} className="tw-text-text-muted tw-flex-shrink-0" />;
            }
            return <ChevronRight size={14} strokeWidth={2} className="tw-text-text-muted tw-flex-shrink-0" />;
          })()}
          <span
            className="tw-truncate"
            style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}
          >
            {number && <span className="tw-mr-2">{number}</span>}
            {module.title}
          </span>
        </div>
        {!aperto && !moduleLocked && daContare.length > 0 && (
          // A modulo chiuso: due segnali indipendenti dal colore — il disegno
          // (disco con spunta / arco / anello vuoto) e il contatore
          // "completate/totale". Cosi' si distingue "finito" da "iniziato"
          // da "mai aperto" senza dover riaprire il modulo per scoprirlo.
          //
          // Un modulo con zero lezioni contabili non mostra niente: un anello
          // a 0% sarebbe un compito da fare che non esiste.
          <div
            className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0"
            data-igea-badge={moduleStatus}
            title={`${moduleStatusLabel} — ${completedCount} di ${daContare.length}`}
          >
            <span
              className="tw-tabular-nums"
              style={{ fontSize: '12px', fontWeight: 500, color: '#6B7280' }}
            >
              {completedCount}/{daContare.length}
            </span>
            <AnelloProgresso
              completate={completedCount}
              totale={daContare.length}
              stato={moduleStatus}
            />
            <span className="sr-only">{moduleStatusLabel}</span>
          </div>
        )}
      </button>

      {aperto && (
        <ul className="tw-list-none tw-m-0 tw-p-0 tw-pb-2">
          {lessons.map((lesson) => {
            const { Icon, color: iconColor } = pickStatusIcon(lesson);
            const isActive = lesson.id === currentUnitId;
            const isLocked = !!lesson.locked;
            const meta = formatMeta(lesson);

            // NOTE: edX global CSS sets `a { color: #006DAA }` with a higher
            // specificity than Tailwind utility classes (which are generated
            // without !important by default). We override via inline style.
            //
            // Active state uses BOTH a soft purple wash background AND a 3px
            // left-border in brand purple. The chromatic-only highlight
            // (background hue) is not robust under deuteranomaly/protanomaly,
            // so the left-border adds a positional/shape signal as a fallback
            // — same pattern as the active row in GitHub's file tree or the
            // selected item in Linear's sidebar. The transparent border on
            // inactive rows keeps the layout from shifting on state change.
            // textDecoration:none is set inline because Open edX's
            // global `a:hover { text-decoration: underline }` rule
            // beats Tailwind utility classes — without this the
            // sidebar lesson titles get an unwanted underline on
            // hover.
            const baseStyle = isActive
              ? {
                color: '#7C3AED',
                background: '#F5F3FF',
                borderLeft: '3px solid #7C3AED',
                paddingLeft: '13px',
                textDecoration: 'none',
              }
              : {
                color: '#1F2937',
                borderLeft: '3px solid transparent',
                paddingLeft: '13px',
                textDecoration: 'none',
              };
            // Locked rows are non-clickable + faded.
            const rowStyle = isLocked
              ? { ...baseStyle, opacity: 0.55, cursor: 'not-allowed' }
              : baseStyle;
            const Tag = isLocked ? 'div' : 'a';
            const tagProps = isLocked
              ? {
                'aria-disabled': 'true',
                title: 'Completa la lezione precedente per sbloccare',
              }
              : { href: lesson.href };
            // `aria-current` dice "sei qui" a chi non vede il colore, ed e'
            // anche il modo con cui la sidebar ritrova questa riga per
            // portarla in vista al cambio di lezione (vedi IgeaSidebar).
            // Un attributo solo per due usi: nessun gancio inventato per il
            // codice che scorre, e la riga attiva smette di essere annunciata
            // come una qualsiasi.
            if (isActive) { tagProps['aria-current'] = 'page'; }

            return (
              <li key={lesson.id} className="tw-m-0">
                <Tag
                  {...tagProps}
                  style={rowStyle}
                  className={[
                    'tw-flex tw-items-start tw-gap-2.5 tw-pr-4 tw-py-2.5 tw-text-sm tw-no-underline tw-transition-colors',
                    isActive || isLocked ? '' : 'hover:tw-bg-igea-bg',
                  ].join(' ')}
                >
                  <Icon
                    size={18}
                    strokeWidth={1.5}
                    style={{
                      color: isActive && lesson.status !== 'completed'
                        ? '#7C3AED'
                        : iconColor,
                    }}
                    className="tw-flex-shrink-0 tw-mt-0.5"
                  />
                  <div className="tw-min-w-0 tw-flex-1">
                    <div
                      className="tw-leading-snug"
                      style={{
                        fontSize: '14px',
                        fontWeight: isActive ? 500 : 400,
                        color: isActive ? '#7C3AED' : '#1F2937',
                        minHeight: '38px',
                      }}
                    >
                      {lesson.title}
                    </div>
                    {meta && (
                      <div
                        className="tw-mt-1 tw-leading-tight"
                        style={{ fontSize: '12px', color: '#9CA3AF' }}
                      >
                        {meta}
                      </div>
                    )}
                  </div>
                </Tag>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const lessonShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  type: PropTypes.string,
  duration: PropTypes.string,
  href: PropTypes.string,
  meta: PropTypes.string,
  questions: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  status: PropTypes.oneOf(['completed', 'in_progress', 'not_started']),
  locked: PropTypes.bool,
  // Falso solo per le lezioni che non hanno nulla da completare: non entrano
  // nell'anello ne' a numeratore ne' a denominatore. Assente = contala.
  completabile: PropTypes.bool,
});

IgeaModuleAccordion.propTypes = {
  number: PropTypes.string,
  module: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    lessons: PropTypes.arrayOf(lessonShape),
    locked: PropTypes.bool,
  }).isRequired,
  currentUnitId: PropTypes.string,
  // Apertura controllata dal chiamante: la sidebar tiene un modulo aperto per
  // volta, e questo componente non puo' saperlo da solo.
  open: PropTypes.bool,
  onToggle: PropTypes.func,
};

IgeaModuleAccordion.defaultProps = {
  number: null,
  currentUnitId: null,
  open: false,
  onToggle: () => {},
};

export default IgeaModuleAccordion;
